// B359 Fase 2 Passo 1 (AI_158) — texto curto, para o médico, do registro do
// verificador em modo SOMBRA (vem em `_proveniencia.sombrasVerificador`, um
// por configuração; `sombraVerificador` = o primeiro, compat. da rodada 1).
// Só informa concordância entre o verificador atual e o candidato; nunca
// altera o selo de fidelidade. Puro e testável.

export interface AchadoSombra {
  regra: number | null;
  severidade?: string | null;
  descricao?: string | null;
  fraseLiteral?: string | null;
  /** true = sobreviveu aos filtros do V1 (contou); false = descartado; undefined = filtros não rodaram */
  contou?: boolean;
}

export interface RegistroSombraVerificador {
  modeloPrincipal?: string | null;
  modeloSombra?: string | null;
  esforco?: string | null;
  principal?: { aprovado: boolean; regras: number[]; nProblemas: number } | null;
  sombra?:
    | {
        ok?: true;
        aprovado: boolean;
        regras: number[];
        nProblemas: number;
        bruto?: { aprovado: boolean; regras: number[]; nProblemas: number } | null;
        aposFiltros?: {
          regras: number[];
          zeradoPorCoerencia?: boolean;
          removidosPorPadrao?: number;
          nAposAncoragem?: number | null;
        } | null;
        achados?: AchadoSombra[];
        duracaoMs?: number | null;
      }
    | { ok: false; motivo?: string; duracaoMs?: number | null }
    | null;
  comparacao?: {
    concorda: boolean;
    aprovadoIgual: boolean;
    soPrincipal: number[];
    soSombra: number[];
  } | null;
}

export const nomeCurtoModelo = (m?: string | null): string => {
  if (!m) return "?";
  const x = m.match(/claude-(haiku|sonnet|opus)-(\d)-(\d)/);
  return x ? `${x[1][0].toUpperCase()}${x[1].slice(1)} ${x[2]}.${x[3]}` : m;
};

const regras = (r?: number[]) => (r && r.length ? `regras [${r.join(", ")}]` : "sem regras");

/** Lista de registros a partir da proveniência (novo array ou campo único antigo). */
export function registrosSombra(
  prov: { sombrasVerificador?: RegistroSombraVerificador[]; sombraVerificador?: RegistroSombraVerificador | null } | null | undefined
): RegistroSombraVerificador[] {
  if (!prov) return [];
  if (Array.isArray(prov.sombrasVerificador) && prov.sombrasVerificador.length > 0) {
    return prov.sombrasVerificador;
  }
  return prov.sombraVerificador ? [prov.sombraVerificador] : [];
}

export function textoSombraVerificador(reg: RegistroSombraVerificador | null | undefined): string | null {
  if (!reg || !reg.modeloSombra) return null;
  const sombra = `${nomeCurtoModelo(reg.modeloSombra)}${reg.esforco ? ` · ${reg.esforco}` : ""}`;
  const atual = nomeCurtoModelo(reg.modeloPrincipal);
  const s = reg.sombra as { ok?: boolean; motivo?: string } | null | undefined;
  if (!s || s.ok === false) {
    return `Verificador em sombra (${sombra}): sem resposta (${(s && s.motivo) || "desconhecido"}).`;
  }
  const c = reg.comparacao;
  if (!c) return `Verificador em sombra (${sombra}): respondeu; sem comparação.`;
  const sb = reg.sombra as { aprovado: boolean; regras: number[]; bruto?: { regras: number[] } | null };
  const brutoNota =
    sb.bruto && sb.bruto.regras && sb.bruto.regras.length > 0 && sb.bruto.regras.length !== (sb.regras || []).length
      ? ` (bruto apontou [${sb.bruto.regras.join(", ")}]; os filtros do V1 deixaram ${regras(sb.regras)})`
      : "";
  if (c.concorda) {
    return `Verificador em sombra (${sombra}) concordou com o ${atual}: ${
      sb.aprovado ? "aprovado" : "reprovado"
    }, ${regras(sb.regras)}${brutoNota}.`;
  }
  const partes: string[] = [];
  if (!c.aprovadoIgual) {
    partes.push(
      `veredito ${atual} ${reg.principal?.aprovado ? "aprovado" : "reprovado"} × ${sombra} ${sb.aprovado ? "aprovado" : "reprovado"}`
    );
  }
  if (c.soPrincipal.length) partes.push(`só o ${atual}: [${c.soPrincipal.join(", ")}]`);
  if (c.soSombra.length) partes.push(`só o ${sombra}: [${c.soSombra.join(", ")}]`);
  return `Verificador em sombra (${sombra}) DIVERGIU — ${partes.join("; ")}${brutoNota}.`;
}

/** Linhas dos achados da sombra (o que ela apontou), para o médico julgar. */
export function linhasAchadosSombra(reg: RegistroSombraVerificador | null | undefined): string[] {
  const s = reg?.sombra as { ok?: boolean; achados?: AchadoSombra[] } | null | undefined;
  if (!s || s.ok === false || !Array.isArray(s.achados)) return [];
  return s.achados.map((a) => {
    const partes: string[] = [];
    partes.push(a.regra != null ? `Regra ${a.regra}` : "Sem regra");
    if (a.severidade) partes.push(`[${a.severidade}]`);
    if (a.descricao) partes.push(a.descricao);
    if (a.fraseLiteral) partes.push(`«${a.fraseLiteral}»`);
    const prefixo = a.contou === true ? "✔ contou — " : a.contou === false ? "✖ descartado pelos filtros do V1 — " : "";
    return prefixo + partes.join(" — ");
  });
}
